import { createMiddleware, createStart } from '@tanstack/react-start'
import { setCookie } from '@tanstack/react-start/server'
import { gpcSignalCookieName } from './lib/anonymous-state'

/**
 * TanStack Start instance (auto-discovered as the `src/start.ts` start entry).
 *
 * It registers a single global REQUEST middleware that runs on every SSR
 * request - documents and server functions alike - so we can honour the
 * browser's Global Privacy Control (GPC) signal at the most reliable layer: the
 * HTTP request header.
 *
 * ── Why the header path ──────────────────────────────────────────────────────
 * GPC is sent two ways: the `navigator.globalPrivacyControl` JS property AND the
 * `Sec-GPC: 1` request header. The JS property is exposed INCONSISTENTLY across
 * browsers (Brave, the most privacy-forward browser, frequently sends the header
 * but does NOT surface the JS property), so a client-only check misses real GPC
 * users and shows them a banner they have legally opted out of. The header is
 * authoritative and always present. We read it here on the server and mirror it
 * into a small, client-READABLE (non-httpOnly) cookie `ae_gpc=1`, which the
 * consent hook then reads on first paint - making the GPC opt-out work on Brave
 * and on statically-prerendered pages where the JS property check alone fails.
 *
 * GPC is a legally recognised opt-out signal under CCPA/CPRA and an increasingly
 * accepted GDPR / Swiss FADP objection. The cookie only records that the signal
 * was present; the actual "necessary only" decision (and never overriding a
 * prior explicit choice) is still owned by `useCookieConsent`.
 */

// 180 days, matching the rest of the app's consent cookies, so the signal is
// remembered across the session even if a later request omits the header.
const GPC_COOKIE_MAX_AGE_SECONDS = 180 * 24 * 60 * 60

export const startInstance = createStart(() => ({
  requestMiddleware: [
    // `createMiddleware()` defaults to a REQUEST middleware (runs on every SSR
    // request, document + server fn), which is exactly the layer we need to read
    // the request header and annotate the response.
    createMiddleware().server(async ({ next, request }) => {
      // `Sec-GPC: 1` is the authoritative GPC opt-out signal. When present, mirror
      // it into a readable cookie the client consent hook can see immediately.
      // Read straight off the incoming request's `Headers` (case-insensitive,
      // accepts any header name) so this works regardless of the typed header map.
      const secGpc = request.headers.get('Sec-GPC')

      if (secGpc === '1') {
        // Non-httpOnly so the client `document.cookie` read in useCookieConsent
        // can honour it; SameSite=Lax + Secure (in prod) for a sane default.
        setCookie(gpcSignalCookieName, '1', {
          path: '/',
          sameSite: 'lax',
          httpOnly: false,
          secure: process.env.NODE_ENV === 'production',
          maxAge: GPC_COOKIE_MAX_AGE_SECONDS,
        })
      }

      // Always continue the chain - this middleware only ANNOTATES the response
      // with the GPC cookie; it must never short-circuit or alter SSR/routing.
      return next()
    }),
  ],
}))
